import { DownOutlined } from "@ant-design/icons";
import { ColDef, CsvExportParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Dropdown, message, Spin } from "antd";
import type { MenuProps } from "antd";
import CiqLogPageHeader from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { GridLoadingIndicator } from "components/widgets";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import {
  ProjectContext,
  TProjectContext,
  isPermissionNotGrantted
} from "context/ProjectProvider";
import { SubscriptionContext } from "context/SubscriptionProvider";
import NotFoundPage from "pages/not-found";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";
import { ActivityLogPrintViewState } from "pages/print-view-layout/model";
import { savePrintViewState } from "pages/print-view-layout/print-view-storage";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useApolloClient } from "@apollo/client";
import { useHistory, useParams } from "react-router";
import { QUERY_GET_AUDIT_LOGS } from "services/graphQL/ciq-queries";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { DateUtils } from "utils/dateutils";
import dayjs from "utils/dayjs-config";
import { ErrorMessages, ProjectPermissionEnum } from "constants/index";
import {
  getActivityLogColumnDefs,
  getActivityLogDefaultColDef,
  ACTIVITY_LOG_DEFAULT_DATE_FILTER,
  resolveActivityLogApiDateFilter,
  formatActivityLogUtcDate,
  formatActivityLogUserExportValue,
  isActivityLogCustomDateFilterValid,
  TActivityLogDateFilterModel,
  TActivityLogRow
} from "./activity-log-columns";

export type { TActivityLogRow } from "./activity-log-columns";

type TAuditLogApiRow = {
  category: string;
  date: string;
  event_type: string;
  file_name: string;
  module: string;
  project_name: string;
  user: string;
  email?: string;
};

const isSameFilterModel = (
  a: Record<string, unknown>,
  b: Record<string, unknown>
) => JSON.stringify(a) === JSON.stringify(b);

const mapAuditLogsToRows = (logs: TAuditLogApiRow[]): TActivityLogRow[] =>
  logs.map((log, index) => ({
    id: `${log.date}-${log.event_type}-${index}`,
    created_at: log.date,
    category: log.category ?? "",
    module: log.module ?? "",
    event_type: log.event_type ?? "",
    user: log.user ?? "",
    email: log.email ?? "",
    file_name: log.file_name ?? "",
    project_name: log.project_name ?? ""
  }));

type ActivityLogViewProps = {
  gqlClient: any;
  clientLoading?: boolean;
  projectId?: string;
  projectName: string;
  backPath: string;
  printViewPath: string;
  canView: boolean;
  entitlementsReady: boolean;
  isExportDisabled?: boolean;
};

function ActivityLogView({
  gqlClient,
  clientLoading = false,
  projectId,
  projectName,
  backPath,
  printViewPath,
  canView,
  entitlementsReady,
  isExportDisabled = false
}: ActivityLogViewProps) {
  const history = useHistory();
  const { saveAuditLogEvent } = useAuditEventLog(gqlClient);
  const gridRef = useRef<AgGridReact<TActivityLogRow>>(null);
  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const [filterModel, setFilterModel] = useState<Record<string, unknown>>(
    () => ({ date_time: resolveActivityLogApiDateFilter(null) })
  );

  // null until the user picks a date filter; the grid starts with none applied.
  const lastAcceptedDateFilterRef = useRef<TActivityLogDateFilterModel | null>(
    null
  );
  // Client-side column filters (Event Type, User, etc.) are lost when AG Grid
  // refreshes rowData after a date-driven API refetch. Snapshot them and
  // re-apply after load; skip snapshot updates while that refetch is in flight.
  const clientSideFiltersRef = useRef<Record<string, unknown>>({});
  const isDateRefetchPendingRef = useRef(false);
  // Seeded once, so clearing the pin stays cleared instead of snapping back.
  const hasSeededDefaultRef = useRef(false);

  const applyFilterModelToApi = useCallback(
    (nextFilterModel: Record<string, unknown>) => {
      // Only the Date column is server-side; other column filters stay on the
      // grid. Ignoring them here keeps Category/User/etc changes from
      // re-running getAuditLog.
      const dateFilter = nextFilterModel.date_time as
        | TActivityLogDateFilterModel
        | undefined;
      const clientFilters = { ...nextFilterModel };
      delete clientFilters.date_time;

      // inRange validation normally blocks apply; keep a safety net so an
      // incomplete or inverted Custom range never hits get_audit_logs.
      if (dateFilter && !isActivityLogCustomDateFilterValid(dateFilter)) {
        const reverted = { ...nextFilterModel };
        if (lastAcceptedDateFilterRef.current) {
          reverted.date_time = lastAcceptedDateFilterRef.current;
        } else {
          delete reverted.date_time;
        }
        gridRef.current?.api?.setFilterModel(reverted);
        return;
      }

      if (!isDateRefetchPendingRef.current) {
        clientSideFiltersRef.current = clientFilters;
      }

      const dateChanged = !isSameFilterModel(
        { date_time: lastAcceptedDateFilterRef.current ?? null },
        { date_time: dateFilter ?? null }
      );
      lastAcceptedDateFilterRef.current = dateFilter ?? null;
      // The API takes a datetime range only, so presets are resolved here; with
      // no filter applied that is the default window.
      const next = { date_time: resolveActivityLogApiDateFilter(dateFilter) };
      setFilterModel((prev) => {
        if (isSameFilterModel(prev, next)) return prev;
        if (dateChanged) {
          clientSideFiltersRef.current = clientFilters;
          isDateRefetchPendingRef.current = true;
        }
        return next;
      });
    },
    []
  );

  const { data: auditLogData, loading } = useCIQQuery<{
    get_audit_logs: TAuditLogApiRow[];
  }>(QUERY_GET_AUDIT_LOGS, {
    client: gqlClient,
    skip: !gqlClient,
    // So filter changes also flip `loading` and show the grid overlay.
    notifyOnNetworkStatusChange: true,
    variables: {
      filter_criteria: {
        filter_model: filterModel
      }
    }
  });

  const rowData = useMemo(
    () => mapAuditLogsToRows(auditLogData?.get_audit_logs ?? []),
    [auditLogData]
  );

  // Include !gqlClient so project-level waits for the project client before
  // showing an empty grid.
  const isGridLoading = clientLoading || !gqlClient || loading;

  useEffect(() => {
    gridRef.current?.api?.setGridOption("loading", isGridLoading);
  }, [isGridLoading]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || isGridLoading) return;

    const currentModel = api.getFilterModel?.() || {};
    const restoredModel: Record<string, unknown> = {
      ...clientSideFiltersRef.current
    };
    if (currentModel.date_time) {
      restoredModel.date_time = currentModel.date_time;
    }

    // Keep the snapshot locked until after restore so a mid-refresh
    // onFilterChanged (date-only model) cannot wipe client filters.
    if (!isSameFilterModel(currentModel, restoredModel)) {
      api.setFilterModel(restoredModel);
      filterChipRef.current?.onFilterApplied();
    }

    isDateRefetchPendingRef.current = false;
    applyFilterModelToApi(restoredModel);
  }, [isGridLoading, rowData, applyFilterModelToApi]);

  const defaultColDef = useMemo<ColDef>(
    () => getActivityLogDefaultColDef(),
    []
  );

  const columnDefs = useMemo<ColDef<TActivityLogRow>[]>(
    () => getActivityLogColumnDefs({ showProjectName: !projectId }),
    [projectId]
  );

  const getExportParams = (fileName: string): CsvExportParams => ({
    columnKeys: gridRef.current?.api
      ?.getAllGridColumns()
      ?.filter((col) => col.isVisible())
      .map((col) => col.getColId()),
    processCellCallback: (params) => {
      if (params.column.getColId() === "date_time") {
        const rawValue =
          (params.node?.data as TActivityLogRow | undefined)?.created_at ??
          params.value;
        const formatted = formatActivityLogUtcDate(rawValue);
        // Leading tab keeps Excel from auto-parsing the cell as a date and
        // dropping AM/PM under some locale/time settings.
        return formatted ? `\t${formatted}` : "";
      }
      if (params.column.getColId() === "user") {
        return formatActivityLogUserExportValue(
          params.node?.data as TActivityLogRow | undefined
        );
      }
      return params.value ?? "";
    },
    fileName
  });

  const exportLogAsCSV = () => {
    const api = gridRef.current?.api;
    if (!api) return;

    const fileName = `Activity_Log_${dayjs().format("YYYY-MM-DD")}`;
    api.exportDataAsCsv(getExportParams(fileName));
    saveAuditLogEvent({
      eventType: "ACTIVITY_LOG_EXPORT_CSV",
      featureType: "PROJECT",
      filename: `${fileName}.csv`
    });
  };

  const onOpenPrintView = useCallback(() => {
    if (!gridRef.current?.api) {
      message.error("Unable to open print view. Grid data is not available.");
      return;
    }

    try {
      const rowsForPrint: TActivityLogRow[] = [];
      gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) {
          rowsForPrint.push(node.data);
        }
      });

      const printViewState: ActivityLogPrintViewState = {
        pageId: "activity-log",
        title: `${
          // Both call sites already default this ("Project" / "Subscription"),
          // so the fallback here can never fire.
          /* v8 ignore next */
          projectName || "Project"
        } - Activity Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        initialState: gridRef.current.api.getState(),
        context: {
          rowData: rowsForPrint
        },
        skipColumnIds: projectId ? ["project_name"] : [],
        headerHeight: 36,
        // Details wraps in print via autoHeight; a fixed height would clip it.
        rowHeight: undefined
      };

      savePrintViewState(printViewState);
      window.open(printViewPath, "_blank");
    } catch (error) {
      console.error("Error opening activity log print view:", error);
      message.error("An error occurred while opening print view.");
    }
  }, [printViewPath, projectId, projectName]);

  const exportMenuItems: MenuProps["items"] = [
    {
      key: "export-csv",
      label: <span data-testid="activity-log-export-csv">Export CSV</span>,
      onClick: exportLogAsCSV,
      disabled: !rowData.length
    },
    {
      key: "export-pdf",
      label: (
        <span data-testid="activity-log-export-pdf">Export PDF/Print</span>
      ),
      onClick: () => {
        if (gridRef.current?.api) {
          PrintViewValidationModal({
            gridApi: gridRef.current.api,
            onProceed: onOpenPrintView
          });
        }
      },
      disabled: !rowData.length
    }
  ];

  const headerItems = [
    <FilterChipComponent
      key="activity-log-filter-chip"
      gridRef={gridRef}
      ref={filterChipRef}
      showDateRangeOnChip
    />,
    <CiqAgSearch
      key="search"
      placeholder="Search user, event type, module..."
      gridRef={gridRef}
      data-testid="activity-log-search-input"
    />,
    <Dropdown
      key="export"
      menu={{ items: exportMenuItems }}
      placement="bottomRight"
      data-testid="activity-log-export-dropdown"
      disabled={!rowData.length || isExportDisabled}
    >
      <Button
        type="primary"
        disabled={!rowData.length || isExportDisabled}
        data-testid="activity-log-export-button"
      >
        Export <DownOutlined />
      </Button>
    </Dropdown>
  ];

  if (!entitlementsReady) {
    return (
      <div className="w-full h-full bg-white flex justify-center items-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!canView) {
    return (
      <NotFoundPage
        status="403"
        title="403"
        subTitle={ErrorMessages.UnauthorizedPageAccess}
      />
    );
  }

  return (
    <div
      className="bg-white h-full w-full flex flex-col"
      data-testid="activity-log-page"
    >
      <CiqLogPageHeader
        entityName="Activity Log"
        items={headerItems}
        backButton={() => {
          history.push(backPath);
        }}
        backButtonTestId="activity-log-back"
      />

      <div className="grow ag-theme-alpine relative min-h-0">
        <AgGridReact<TActivityLogRow>
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={36}
          rowHeight={42}
          loading={isGridLoading}
          loadingOverlayComponent={GridLoadingIndicator}
          suppressContextMenu
          tooltipShowMode="whenTruncated"
          onFilterChanged={(e) => {
            applyFilterModelToApi(e.api.getFilterModel?.() || {});
            filterChipRef.current?.onFilterApplied();
          }}
          onGridReady={(params) => {
            // params.api is set before the ref is assigned, so seeding must not
            // depend on gridRef being populated yet.
            const api = params.api ?? gridRef.current?.api;
            if (!api) return;
            api.setGridOption("loading", isGridLoading);
            api.sizeColumnsToFit();
            const currentModel = api.getFilterModel?.() || {};
            if (!hasSeededDefaultRef.current && !currentModel.date_time) {
              hasSeededDefaultRef.current = true;
              const seeded = {
                ...currentModel,
                date_time: ACTIVITY_LOG_DEFAULT_DATE_FILTER
              };
              // setFilterModel resolves a tick later; the chip has to read the
              // model after it lands or it renders as though nothing is applied.
              Promise.resolve(api.setFilterModel(seeded)).then(() => {
                filterChipRef.current?.onFilterApplied();
              });
              applyFilterModelToApi(seeded);
            } else {
              applyFilterModelToApi(currentModel);
            }
            filterChipRef.current?.onFilterApplied();
          }}
        />
      </div>
    </div>
  );
}

function ProjectLevelActivityLog() {
  const { projectId } = useParams() as { projectId: string };
  const {
    tokenContents,
    projectDetails,
    gqlClientForProject,
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable,
    isProjectArchived
  }: TProjectContext = useContext(ProjectContext);

  const cannotViewProjectReports = tokenContents
    ? isPermissionNotGrantted(
        ProjectPermissionEnum.ViewProjectReports,
        tokenContents?.role
      )
    : false;

  const cannotViewActivityLog =
    cannotViewProjectReports ||
    (isProductEntitlementsDataAvailable &&
      !isFeatureFlagEnabled("ACTIVITY_LOG"));

  return (
    <ActivityLogView
      gqlClient={gqlClientForProject}
      projectId={projectId}
      projectName={projectDetails?.name || "Project"}
      backPath={`/project/${projectId}/reports/audit-reports`}
      printViewPath={`/project/${projectId}/printView`}
      canView={!cannotViewActivityLog}
      entitlementsReady={isProductEntitlementsDataAvailable}
      isExportDisabled={!!isProjectArchived}
    />
  );
}

function SubscriptionLevelActivityLog() {
  // HomeLayout ApolloProvider is built with the subscription-level token.
  const subscriptionGqlClient = useApolloClient();
  const { isFeatureFlagEnabled, isProductEntitlementsDataAvailable } =
    useContext(SubscriptionContext);

  return (
    <ActivityLogView
      gqlClient={subscriptionGqlClient}
      projectName="Subscription"
      backPath="/reports"
      printViewPath="/reports/printView"
      canView={isFeatureFlagEnabled("SUBSCRIPTION_ACTIVITY_LOG")}
      entitlementsReady={isProductEntitlementsDataAvailable}
    />
  );
}

function ActivityLogReport() {
  const { projectId } = useParams() as { projectId?: string };
  if (projectId) {
    return <ProjectLevelActivityLog />;
  }
  return <SubscriptionLevelActivityLog />;
}

export default ActivityLogReport;
